import React, {useMemo, useState} from 'react';
import Select from 'react-select';
import {
  Modal,
  PrimaryButton,
  customStyles,
  DropdownIndicator,
} from 'components';
import ReactFlagsSelect from 'react-flags-select';
import {useMutation} from 'react-query';
import {amlScreening} from 'requests/queries/aml';
import {useAllApps} from 'hooks/useApps';
import {toast} from 'react-toastify';

function SingleSearchModal({
  show,
  onClose,
  queryClient,
  onSearchSuccess,
  user,
}) {
  const [selectedCountry, setSelectedCountry] = useState('NG');
  const [entityType, setEntityType] = useState('individual');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    entityName: '',
    registrationNumber: '',
    yearOfBirth: '',
    caseId: '',
    matchScore: 85,
    categories: {pep: true, adverseMedia: true, sanctionsCriminals: true},
  });

  const {userApps, isLoading: appsLoading} = useAllApps();
  const appIds = useMemo(
    () => (!appsLoading && userApps.length > 0 ? userApps[0]?.value : ''),
    [appsLoading, userApps],
  );

  const mutation = useMutation({
    mutationFn: amlScreening,
    onSuccess: () => {
      toast.success('Search completed successfully!');
      queryClient.invalidateQueries(['all-searches']);
      onSearchSuccess();
      onClose();
    },
    onError: error => {
      console.error('AML Screening error:', error);
      toast.error('An error occurred during the search.');
    },
  });

  const handleChange = (field, value) => {
    if (field === 'matchScore') {
      if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
        setFormData(prev => ({...prev, [field]: value}));
      }
    } else if (field in formData.categories) {
      setFormData(prev => ({
        ...prev,
        categories: {...prev.categories, [field]: !prev.categories[field]},
      }));
    } else {
      setFormData(prev => ({...prev, [field]: value}));
    }
  };

  const handleEntityTypeChange = ({value}) => {
    setEntityType(value);
    setFormData(prev => ({
      ...prev,
      firstName: '',
      lastName: '',
      entityName: '',
      registrationNumber: '',
      yearOfBirth: '',
    }));
  };

  const validateForm = () => {
    const {firstName, lastName, entityName} = formData;
    if (entityType === 'individual') {
      if (!firstName.trim())
        return toast.error('Please enter a first name.'), false;
      if (!lastName.trim())
        return toast.error('Please enter a last name.'), false;
    } else if (!entityName.trim()) {
      return toast.error('Please enter an organization name.'), false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const createdBy =
      user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`.trim()
        : 'Unknown User';

    const payload = {
      app_id: appIds,
      properties:
        entityType === 'individual'
          ? {
              names: `${formData.firstName} ${formData.lastName}`.trim(),
              gender: 'male',
              date_of_birth: formData.yearOfBirth || undefined,
              nationality: selectedCountry || undefined,
            }
          : {
              names: formData.entityName,
              registration_number: formData.registrationNumber
                ? [formData.registrationNumber]
                : undefined,
              country_of_incorporation: selectedCountry
                ? [selectedCountry]
                : undefined,
            },
      screening_options: {
        pep_check: formData.categories.pep,
        sanction: formData.categories.sanctionsCriminals,
        adverse_media_check: formData.categories.adverseMedia,
        watchlists: [],
        match_threshold: formData.matchScore
          ? parseFloat(formData.matchScore) / 100
          : 0.85,
      },
      schema: entityType,
      unique_reference: formData.caseId || undefined,
      created_by: createdBy,
    };

    mutation.mutate({data: payload, created_by: createdBy});
  };

  const entityTypeOptions = [
    {value: 'individual', label: 'Individual'},
    {value: 'organization', label: 'Organization'},
  ];

  const renderInput = (
    label,
    field,
    placeholder,
    required = false,
    type = 'text',
    props = {},
  ) => (
    <div>
      <label className="text-tiny text-[#4E4E4E] mb-[6px] font-normal flex flex-row gap-1">
        {label}
        {required && <span className="text-red">*</span>}
      </label>
      <input
        type={type}
        value={formData[field]}
        onChange={e => handleChange(field, e.target.value)}
        placeholder={placeholder}
        className="block w-full outline-[#D5D5D5] h-9 rounded-md text-[12px] text-[#959595]"
        disabled={mutation.isLoading}
        {...props}
      />
    </div>
  );

  const renderCheckbox = (label, id, field) => (
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id={id}
        checked={formData.categories[field]}
        onChange={() => handleChange(field)}
        className="w-4 h-4"
        disabled={mutation.isLoading}
      />
      <label htmlFor={id} className="text-tiny text-[#080917] font-normal">
        {label}
      </label>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      modalTitle="Single Search"
      extraModalClasses="lg:max-w-[800px] max-w-full rounded-none"
      modalPosition="items-start justify-end !p-0"
    >
      <div className="bg-[#F6F7F9] pt-5 pl-7 pb-9 pr-10">
        <div className="mb-4 text-left">
          <label className="text-tiny text-[#4E4E4E] mb-[6px] font-normal">
            Entity type
          </label>
          <Select
            defaultValue={entityTypeOptions.find(
              option => option.value === entityType,
            )}
            value={entityTypeOptions.find(
              option => option.value === entityType,
            )}
            onChange={handleEntityTypeChange}
            options={entityTypeOptions}
            placeholder="Select entity type"
            components={{DropdownIndicator}}
            classNamePrefix="react-select"
            styles={customStyles}
            menuPlacement="auto"
            isSearchable={false}
            isDisabled={mutation.isLoading}
            id="entity-type"
          />
        </div>

        {entityType === 'individual' ? (
          <div className="grid grid-cols-2 gap-4">
            {renderInput('First name', 'firstName', 'Enter first name', true)}
            {renderInput('Last name', 'lastName', 'Enter last name', true)}
          </div>
        ) : (
          renderInput(
            'Organization name',
            'entityName',
            'Enter organization name',
          )
        )}

        <div className="grid grid-cols-2 gap-4 mt-4">
          {entityType === 'individual'
            ? renderInput('Date of birth', 'yearOfBirth', 'DD - MM - YYYY')
            : renderInput(
                'Registration number',
                'registrationNumber',
                'Enter registration number',
              )}
          <div>
            <label className="text-tiny text-[#4E4E4E] mb-[6px] font-normal">
              {entityType === 'individual'
                ? 'Nationality'
                : 'Country of incorporation'}
            </label>
            <ReactFlagsSelect
              className="flag-select"
              selected={selectedCountry}
              onSelect={code => setSelectedCountry(code)}
              disabled={mutation.isLoading}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          {renderInput(
            'Match score',
            'matchScore',
            'Enter score (0 to 100)',
            false,
            'number',
            {
              step: '1',
              min: '0',
              max: '100',
            },
          )}
          {renderInput('Case ID', 'caseId', 'Input case ID')}
        </div>

        <div className="mt-6">
          <h3 className="text-tiny font-medium text-[#4A4A68] text-start">
            Categories
          </h3>
          <div className="grid grid-cols-3 gap-4 mt-4">
            {renderCheckbox('PEP', 'pep', 'pep')}
            {renderCheckbox('ADVERSE media', 'adverse-media', 'adverseMedia')}
            {renderCheckbox(
              'SANCTIONS & CRIMINALS',
              'sanctions-criminals',
              'sanctionsCriminals',
            )}
          </div>
        </div>
      </div>
      <div className="py-6 flex items-center justify-center">
        <PrimaryButton
          onClick={handleSubmit}
          buttonText={mutation.isLoading ? 'Searching...' : 'Perform Search'}
          className="lg:w-[455px] !h-12"
          disabled={mutation.isLoading}
        />
      </div>
    </Modal>
  );
}

export default SingleSearchModal;
